import { DetailPageAccessMode } from '@app/detailPageLayout.models';
import { FETCH_QUERIES_NAME } from '@common/constants';
import { LabelWithMessage } from '@common/LabelWithMessage';
import { IProjectCompanyOption } from '@company/projectCompany/projectCompany.models';
import { useProjectId } from '@config/config.hooks';
import { AxiosErrorResponse } from '@core/error.models';
import { FormTextareaWithMaxLength } from '@core/FormTextareaWithMaxLength';
import {
  isDesignModificationStatusInitial,
  useAllowEditNominatedDesigner,
  useIsAttachmentUploadLocked,
  useIsProposedModificationAttachmentUploadEnabled,
  useIsRiskCategoryEnabled,
  useT3DetailGeneralFieldsEnabled,
} from '@designModification/designModification.hooks';
import {
  EDesignModificationFormKeys,
  EDesignModificationIssueRole,
  IProjectDesignModification,
} from '@designModification/designModification.models';
import { FormDrawingSelect } from '@drawing/FormDrawingSelect';
import { useFetchDrawingSearch } from '@drawing/projectDrawingRegister.queries';
import { Attachments } from '@file/Attachments';
import { EProjectFileType } from '@file/projectFile/projectFile.constants';
import { IProjectFileAttachmentsDesignModificationsRequest } from '@file/projectFile/projectFile.models';
import { useFetchAttachmentDesignModification } from '@file/projectFile/projectFile.queries';
import {
  CheckboxType,
  ContentLoader,
  FormCheckboxGroup,
  FormDatePicker,
  FormFieldMethods,
  FormInput,
  FormSelect,
  Grid,
  InputType,
  requiredRule,
} from '@gcl/react-core';
import { IMasterScheduleDetail } from '@masterSchedule/detail/masterScheduleDetail.models';
import { useIsDesignerNotApplicable } from '@masterSchedule/masterSchedule.hooks';
import { IProjectTeamOption } from '@projectTeam/projectTeam.models';
import { useFetchReasonOfChangeOptions } from '@reasonOfChange/reasonOfChange.queries';
import { IRiskCategory } from '@riskCategory/riskCategory.models';
import { useFetchAllRiskCategories } from '@riskCategory/riskCategory.queries';
import { UseQueryResult } from '@tanstack/react-query';
import React, { useEffect } from 'react';

interface IDesignModificationDetailTabFormProps {
  formFieldMethods: FormFieldMethods;
  mode: DetailPageAccessMode;
  designModificationId?: number;
  designModification?: IProjectDesignModification;
  masterSchedule?: IMasterScheduleDetail;
  masterScheduleId?: number;
  isReadOnly: boolean;
  checkerCompanyOptions?: IProjectCompanyOption[];
  checkerTeamOptions?: IProjectTeamOption[];
  designerCompanyOptions?: IProjectCompanyOption[];
  designerTeamOptions?: IProjectTeamOption[];
  searchParamIssueRole?: EDesignModificationIssueRole;
  isSuperseded: boolean;
}
export const DesignModificationDetailTabForm = ({
  formFieldMethods,
  mode,
  masterSchedule,
  masterScheduleId,
  designModification,
  designModificationId,
  isReadOnly,
  checkerCompanyOptions,
  checkerTeamOptions,
  designerCompanyOptions,
  designerTeamOptions,
  searchParamIssueRole,
  isSuperseded,
}: IDesignModificationDetailTabFormProps) => {
  const projectId = useProjectId();
  const {
    formState: { isDirty },
  } = formFieldMethods;

  const isAttachmentUploadLocked = useIsAttachmentUploadLocked(masterSchedule);
  const isProposedModificationAttachmentUploadEnabled = useIsProposedModificationAttachmentUploadEnabled(
    designModification,
    masterSchedule,
  );
  const isRiskCategoryEnabled = useIsRiskCategoryEnabled();
  const isGeneralFieldsEnabled = useT3DetailGeneralFieldsEnabled(
    isSuperseded,
    designModification,
    masterSchedule,
    searchParamIssueRole,
  );

  const isDesignerNotApplicable = useIsDesignerNotApplicable(masterSchedule);
  const isEditNominatedDesignerAllowed = useAllowEditNominatedDesigner();

  const selectedCheckerCompany: IProjectCompanyOption | undefined = formFieldMethods.watch(
    EDesignModificationFormKeys.NOMINATED_CHECKER_COMPANY,
  );
  const selectedDesignerCompany: IProjectCompanyOption | undefined = formFieldMethods.watch(
    EDesignModificationFormKeys.NOMINATED_DESIGNER_COMPANY,
  );

  const { data: dataRiskCategories } = (
    useFetchAllRiskCategories({ isDirty }) as () => UseQueryResult<IRiskCategory[], AxiosErrorResponse>
  )();

  const { data: reasonOfChangeOptions, isLoading: isLoadingReasonOfChangeOptions } = useFetchReasonOfChangeOptions({})({
    projectId,
  });

  useEffect(() => {
    if (!isDirty) return;
    formFieldMethods.setValue(EDesignModificationFormKeys.NOMINATED_CHECKER, undefined);
  }, [JSON.stringify(selectedCheckerCompany)]);

  useEffect(() => {
    if (!isDirty) return;
    formFieldMethods.setValue(EDesignModificationFormKeys.NOMINATED_DESIGNER, undefined);
  }, [JSON.stringify(selectedDesignerCompany)]);

  return (
    <Grid container spacing={2} className="gcl-my-sm">
      <Grid item xs={12} md={6}>
        <LabelWithMessage labelFor={EDesignModificationFormKeys.LOCATION} text="Location" required />
        <FormInput
          rules={requiredRule}
          type={InputType.Text}
          disabled={isReadOnly || !isGeneralFieldsEnabled}
          id={EDesignModificationFormKeys.LOCATION}
          formFieldMethods={formFieldMethods}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <LabelWithMessage labelFor={EDesignModificationFormKeys.ELEMENT_OF_WORKS} text="Element of works" required />
        <FormInput
          rules={requiredRule}
          type={InputType.Text}
          disabled={isReadOnly || !isGeneralFieldsEnabled}
          id={EDesignModificationFormKeys.ELEMENT_OF_WORKS}
          formFieldMethods={formFieldMethods}
        />
      </Grid>
      <Grid item xs={12}>
        <LabelWithMessage
          labelFor={EDesignModificationFormKeys.CHANGE_DETAIL}
          message="If this form has insufficient space, please elaborate further in an attachment."
          text="Details of proposed design change"
          required
        />
        <FormTextareaWithMaxLength
          defaultNumOfLines={5}
          rules={requiredRule}
          disabled={isReadOnly || !isGeneralFieldsEnabled}
          max={1200}
          id={EDesignModificationFormKeys.CHANGE_DETAIL}
          formFieldMethods={formFieldMethods}
        />
      </Grid>
      <Grid item xs={12}>
        <LabelWithMessage
          labelFor={EDesignModificationFormKeys.PROPOSED_MODIFICATION_FILES}
          message={`This attachment does not form part of the final T3 and will not be released for construction.\nThe TWD must upload an attachment to 'Designer Attachment (Completed Modification)' below.`}
          text="Proposed modification attachment"
        />
        <Attachments<IProjectFileAttachmentsDesignModificationsRequest>
          fileType={EProjectFileType.T3_SITE_OPERATION}
          mode={isAttachmentUploadLocked || !isProposedModificationAttachmentUploadEnabled ? 'View' : mode}
          id={EDesignModificationFormKeys.PROPOSED_MODIFICATION_FILES}
          uploadFileRequestParam={{ designModificationId }}
          useFetchFilesQueryKey={[
            projectId,
            designModificationId,
            FETCH_QUERIES_NAME.PROJECT_FILE_ATTACHMENTS_DESIGN_MODIFICATIONS,
            EProjectFileType.T3_SITE_OPERATION,
          ]}
          useFetchFiles={useFetchAttachmentDesignModification}
          useFetchFilesProps={{
            designModificationId: designModificationId ?? 0,
            projectId,
            fileType: EProjectFileType.T3_SITE_OPERATION,
          }}
          formFieldMethods={formFieldMethods}
          formKeyToUpdateInCreateMode={EDesignModificationFormKeys.PROPOSED_MODIFICATION_FILES}
        />
      </Grid>
      <Grid item xs={12}>
        <LabelWithMessage
          labelFor={EDesignModificationFormKeys.REASONS_OF_CHANGE}
          text="Reason(s) for Change"
          required
        />
        <ContentLoader
          active={isLoadingReasonOfChangeOptions}
          linePadding={10}
          paragraphConfigs={[{ suffixBlankRow: false, numberOfRows: 5, lineHeight: 18 }]}
        >
          <FormCheckboxGroup
            formFieldMethods={formFieldMethods}
            disabled={isReadOnly || !isGeneralFieldsEnabled}
            id={EDesignModificationFormKeys.REASONS_OF_CHANGE}
            itemPerRow={3}
            displayFieldName="reasonOfChangeDesc"
            type={CheckboxType.Checkbox}
            checkOptions={reasonOfChangeOptions || []}
            className="gcl-gems-design-modification-details-checkbox-group"
            rules={{
              validate: (value: any) => {
                if (value && value.length > 0) return true;
                return 'At least one should be selected';
              },
            }}
          />
        </ContentLoader>
      </Grid>
      <Grid item xs={12} md={6}>
        <LabelWithMessage
          labelFor={EDesignModificationFormKeys.REQUIRED_APPROVAL_DATE}
          text="Required approval date"
          required
        />
        <FormDatePicker
          rules={requiredRule}
          formFieldMethods={formFieldMethods}
          id={EDesignModificationFormKeys.REQUIRED_APPROVAL_DATE}
          disabled={
            isReadOnly || !isGeneralFieldsEnabled || !isDesignModificationStatusInitial(designModification?.status)
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <LabelWithMessage
          labelFor={EDesignModificationFormKeys.RISK_CATEGORY}
          message="Can only be changed by EM"
          text="T3 risk category"
          required
        />
        <FormSelect
          options={dataRiskCategories ?? []}
          displayFieldName="category"
          formFieldMethods={formFieldMethods}
          id={EDesignModificationFormKeys.RISK_CATEGORY}
          disabled={isReadOnly || !isRiskCategoryEnabled}
          clearable={false}
          allowSearch={false}
          rules={requiredRule}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <LabelWithMessage
          labelFor={EDesignModificationFormKeys.NOMINATED_CHECKER_COMPANY}
          message="Can only be changed by EM/TWC"
          text="Nominated checker"
          required
        />

        <FormSelect
          options={checkerCompanyOptions || []}
          displayFieldName="name"
          formFieldMethods={formFieldMethods}
          id={EDesignModificationFormKeys.NOMINATED_CHECKER_COMPANY}
          disabled
          clearable={false}
          allowSearch={false}
          rules={requiredRule}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <LabelWithMessage
          labelFor={EDesignModificationFormKeys.NOMINATED_CHECKER}
          message="Can only be changed by EM/TWC"
          text="Nominated checker team"
          required
        />

        <FormSelect
          options={checkerTeamOptions || []}
          displayFieldName="teamName"
          formFieldMethods={formFieldMethods}
          id={EDesignModificationFormKeys.NOMINATED_CHECKER}
          disabled
          clearable={false}
          allowSearch={false}
          rules={requiredRule}
        />
      </Grid>
      {isDesignerNotApplicable && (
        <>
          <Grid item xs={12} md={6}>
            <LabelWithMessage
              labelFor={EDesignModificationFormKeys.NOMINATED_DESIGNER_COMPANY}
              text="Nominated designer"
              required={isDesignerNotApplicable}
              testId="t3-form-designer-company"
            />

            <FormSelect
              options={designerCompanyOptions || []}
              displayFieldName="name"
              formFieldMethods={formFieldMethods}
              id={EDesignModificationFormKeys.NOMINATED_DESIGNER_COMPANY}
              disabled={
                isReadOnly || !isEditNominatedDesignerAllowed || !isGeneralFieldsEnabled || !isDesignerNotApplicable
              }
              clearable={false}
              allowSearch={false}
              rules={{
                ...(isDesignerNotApplicable && requiredRule),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <LabelWithMessage
              labelFor={EDesignModificationFormKeys.NOMINATED_DESIGNER}
              text="Nominated designer team"
              required={isDesignerNotApplicable}
              testId="t3-form-designer-team"
            />
            <FormSelect
              options={designerTeamOptions || []}
              displayFieldName="teamName"
              formFieldMethods={formFieldMethods}
              id={EDesignModificationFormKeys.NOMINATED_DESIGNER}
              disabled={isReadOnly || !isEditNominatedDesignerAllowed || !isGeneralFieldsEnabled}
              clearable={false}
              allowSearch={false}
              rules={{
                ...(isDesignerNotApplicable && requiredRule),
              }}
            />
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <LabelWithMessage
          labelFor={EDesignModificationFormKeys.DRAWINGS_MODIFIED}
          message="A T3 modifies drawings which have been Released for Construction. You must identify which drawings are being modified."
          text="Drawings to be modified by T3"
          required
        />
        <FormDrawingSelect
          rules={requiredRule}
          formFieldMethods={formFieldMethods}
          id={EDesignModificationFormKeys.DRAWINGS_MODIFIED}
          isReadOnly={isReadOnly || !isGeneralFieldsEnabled}
          useFetchSearch={useFetchDrawingSearch}
          masterScheduleId={masterScheduleId}
        />
      </Grid>
    </Grid>
  );
};
